import { SkyDialog, SkyInput, SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import { webhookUrlSchema } from "@shared/types/settings";
import { ErrorState } from "./ScreenState";

/**
 * Replace a secret-valued setting (SEC-2, FR-72, canvas 14 panel 05).
 *
 * The value travels in ONE direction. It is written to Key Vault first, and
 * only when the vault confirms does the row point at the new version; the
 * response carries `{ vaultName, secretName }` and never a value, so after
 * saving the field reads "Stored" and the URL cannot be recovered from this
 * screen. The dialog says so before the reader types, not after.
 *
 * The audit event records that the webhook changed, never what it changed to.
 */
export function ReplaceSecretDialog({
  open,
  onOpenChange,
  label,
  description,
  onSubmit,
  isSaving,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  description: string;
  onSubmit: (value: string) => void;
  isSaving: boolean;
  error: unknown;
}) {
  // Starts empty on every opening because the caller remounts it with a key.
  // That matters more here than on an ordinary form: a secret left in a field
  // across two openings would be re-submitted by the next Save.
  const [value, setValue] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  function submit() {
    const parsed = webhookUrlSchema.safeParse(value.trim());
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? "Enter an absolute https URL.");
      return;
    }
    setFieldError(null);
    onSubmit(parsed.data);
  }

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Replace the ${label.toLowerCase()}`}
      description={description}
      size="medium"
      testId="replace-secret-dialog"
      // SkyDialog closes itself on a primary action, which would throw away a
      // form the server has just refused. The dialog closes when the mutation
      // succeeds, and only then; a failure leaves the typing where it is.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={{ label: "Write to vault", onClick: submit, loading: isSaving }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-4">
        {error ? <ErrorState title="The secret was not written" error={error} /> : null}

        <SkyInput
          label={label}
          type="url"
          required
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="https://example.webhook.office.com/webhookb2/…"
          validationState={fieldError ? "error" : "none"}
          validationMessage={fieldError ?? undefined}
          testId="secret-value"
        />

        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="The value is never shown again"
          message="After saving, this setting reads “Stored” with its vault reference. The audit event records that it changed, not what it changed to."
        />
      </div>
    </SkyDialog>
  );
}
